import { useEffect, useState } from 'react'

import { DataService } from 'api/DataService'
import * as yup from 'yup'

import { CustomFieldItem } from 'components/CustomFieldRenderer'

import { isValidISODate, normalizeDateType } from 'utils/custom-field-date'
import {
  hasUploadValue,
  normalizeUploadSubmitValue,
  UploadFileType,
  UploadSettings
} from 'utils/customFieldUpload'

interface CustomFieldConfig {
  uuid: string
  context_type: string
  required: boolean
  position: number
  custom_field: {
    uuid: string
    field_type: string
    field_configuration: {
      type: string
      title: string
      value: string
      description: string
      options?: Array<{
        uuid: string
        label: string
        value: string
      }>
      display_mode?: 'list' | 'dropdown'
      accepted_types?: UploadFileType[]
      multiple?: boolean
      max_files?: number
    }
    upload_settings?: UploadSettings
  }
}

export type ContextType = 'customer' | 'internal_customer' | 'product_link'

interface UseCustomFieldsProps {
  contextType?: ContextType
  entityUuid?: string
  fieldNamePrefix?: string
  autoLoad?: boolean
  excludeFieldTypes?: string[]
  uuid?: string // UUID do merchant para páginas públicas
}

interface UseCustomFieldsReturn {
  customFields: CustomFieldItem[]
  loading: boolean
  error: string | null
  loadCustomFields: () => Promise<void>
  generateValidationSchema: (baseSchema?: any) => any
  processFormData: (formData: any) => {
    customFieldValues: Array<{
      custom_field_uuid: string
      value: any
    }>
    cleanedFormData: any
  }
  populateFormValues: (existingValues: any[]) => Record<string, any>
  saveConfiguration: (
    configs: Array<{
      custom_field_uuid: string
      required: boolean
      position: number
    }>
  ) => Promise<boolean>
}

export const useCustomFields = ({
  contextType = 'customer',
  entityUuid,
  fieldNamePrefix = 'extraField',
  autoLoad = true,
  excludeFieldTypes = [],
  uuid
}: UseCustomFieldsProps = {}): UseCustomFieldsReturn => {
  const [customFields, setCustomFields] = useState<CustomFieldItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const loadCustomFields = async () => {
    try {
      setLoading(true)
      setError(null)

      let url: string

      // Se uuid for fornecido, usar a URL pública para merchants
      if (uuid) {
        url = `/v1/merchants/${uuid}/customer_fields/configs`
      } else {
        // Usar a URL padrão que requer token
        url = `/v1/custom_fields/configs?context_type=${contextType}&per=100`
        if (entityUuid) {
          url += `&entity_uuid=${entityUuid}`
        }
      }

      const response = await DataService({
        type: 'GET',
        url
      })

      if (response.status === 200 && response.data?.records) {
        const configs = response.data.records as CustomFieldConfig[]
        const items: CustomFieldItem[] = configs
          .sort((a, b) => (a.position || 0) - (b.position || 0))
          .map((config) => ({
            uuid: config.custom_field.uuid,
            custom_field: config.custom_field,
            name: config.custom_field.field_configuration.title,
            required: config.required,
            field_type: config.custom_field.field_type,
            description: config.custom_field.field_configuration.description,
            options:
              config.custom_field.field_configuration.options?.map((opt) => ({
                label: opt.label,
                value: opt.value
              })) || [],
            display_mode: config.custom_field.field_configuration.display_mode
          }))

        setCustomFields(items)
      } else {
        setCustomFields([])
      }
    } catch (err) {
      console.error('Erro ao carregar campos customizados:', err)
      setError('Erro ao carregar campos customizados')
      setCustomFields([])
    } finally {
      setLoading(false)
    }
  }

  const generateValidationSchema = (baseSchema?: any) => {
    const customFieldValidations: any = {}

    // Filtrar campos excluídos antes de criar validação
    const fieldsToValidate = customFields.filter(
      (field) => !excludeFieldTypes.includes(field.field_type)
    )

    fieldsToValidate.forEach((field) => {
      const fieldName = `${fieldNamePrefix}_${field.uuid}`

      switch (field.field_type) {
        case 'short_text':
        case 'long_text':
          customFieldValidations[fieldName] = field.required
            ? yup.string().nullable().required(`Campo obrigatório`)
            : yup.string().nullable().optional()
          break
        case 'checkbox':
          customFieldValidations[fieldName] = field.required
            ? yup
                .array()
                .nullable()
                .min(1, `Campo obrigatório`)
                .required(`Campo obrigatório`)
            : yup.array().nullable().optional()
          break
        case 'radio':
          customFieldValidations[fieldName] = field.required
            ? yup.string().nullable().required(`Campo obrigatório`)
            : yup.string().nullable().optional()
          break
        case 'upload':
          // Valor é o uuid do arquivo ou a lista de uuids (vários arquivos)
          customFieldValidations[fieldName] = field.required
            ? yup
                .mixed()
                .nullable()
                .test('upload-required', 'Campo obrigatório', (value) =>
                  hasUploadValue(value)
                )
            : yup.mixed().nullable().optional()
          break
        case 'date': {
          const dateType = normalizeDateType(
            field.custom_field?.field_configuration?.type
          )
          const dateSchema = yup
            .string()
            .nullable()
            .test(
              'valid-date',
              'Data inválida',
              (value) => !value || isValidISODate(value, dateType)
            )
          customFieldValidations[fieldName] = field.required
            ? dateSchema.required(`Campo obrigatório`)
            : dateSchema.optional()
          break
        }
        case 'accept':
          // Campos de aceite são sempre obrigatórios, independente da configuração
          // MAS apenas se não estiverem excluídos
          if (!excludeFieldTypes.includes('accept')) {
            customFieldValidations[fieldName] = yup
              .boolean()
              .oneOf([true], 'Você deve aceitar os termos e condições')
              .required('Você deve aceitar os termos e condições')
          }
          break
        default:
          customFieldValidations[fieldName] = field.required
            ? yup.string().nullable().required(`Campo obrigatório`)
            : yup.string().nullable().optional()
      }
    })

    if (baseSchema) {
      return baseSchema.concat(yup.object(customFieldValidations))
    }

    return yup.object(customFieldValidations)
  }

  const processFormData = (formData: any) => {
    const customFieldValues: Array<{
      custom_field_uuid: string
      value: any
    }> = []

    const cleanedFormData = { ...formData }

    customFields.forEach((field) => {
      const fieldName = `${fieldNamePrefix}_${field.uuid}`
      const fieldValue = formData[fieldName]

      // Campos de aceite e upload sempre devem ser processados, mesmo que false/empty
      const shouldProcess =
        field.field_type === 'accept' ||
        field.field_type === 'upload' ||
        (fieldValue !== undefined && fieldValue !== null && fieldValue !== '')

      if (shouldProcess) {
        let processedValue = fieldValue

        if (field.field_type === 'checkbox' && Array.isArray(fieldValue)) {
          processedValue = fieldValue.map((value) => {
            const option = field.options?.find((opt) => opt.value === value)
            return option
              ? { label: option.label, value: option.value }
              : { label: value, value }
          })
        } else if (field.field_type === 'radio') {
          const option = field.options?.find((opt) => opt.value === fieldValue)
          processedValue = option
            ? { label: option.label, value: option.value }
            : String(fieldValue)
        } else if (field.field_type === 'accept') {
          // Para campos de aceite, sempre enviar o valor boolean
          processedValue = Boolean(fieldValue)
        } else if (field.field_type === 'upload') {
          processedValue = normalizeUploadSubmitValue(fieldValue)
        } else {
          processedValue = String(fieldValue)
        }

        customFieldValues.push({
          custom_field_uuid: field.uuid,
          value: processedValue
        })
      }
      delete cleanedFormData[fieldName]
    })

    return {
      customFieldValues,
      cleanedFormData
    }
  }

  const populateFormValues = (existingValues: any[]) => {
    const formValues: Record<string, any> = {}

    existingValues.forEach((fieldData) => {
      const fieldName = `${fieldNamePrefix}_${fieldData.uuid}`
      let value = fieldData.value || fieldData.field_value

      // Para campos checkbox e radio, manter o formato de objeto {label, value}
      // que é esperado pelo CustomFieldRenderer
      if (fieldData.field_type === 'checkbox' && Array.isArray(value)) {
        // Manter como array de objetos {label, value}
        value = value.map((item: any) => {
          if (typeof item === 'object' && item.label && item.value) {
            return item // Já está no formato correto
          } else if (typeof item === 'string') {
            // Converter string para objeto (compatibilidade com formato antigo)
            const options =
              fieldData.options ||
              fieldData.custom_field?.field_configuration?.options ||
              []
            const option = options.find((opt: any) => opt.value === item)
            return option
              ? { label: option.label, value: option.value }
              : { label: item, value: item }
          }
          return item
        })
      } else if (fieldData.field_type === 'radio' && value) {
        // Para radio, manter como objeto {label, value}
        if (typeof value === 'string') {
          // Verificar se é Ruby hash format
          if (value.includes('=>')) {
            try {
              // Converter Ruby hash format para JSON
              const jsonString = value
                .replace(/=>/g, ':')
                .replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":')
              value = JSON.parse(jsonString)
            } catch (e) {
              console.warn('Erro ao parsear valor Ruby hash:', value)
              // Fallback para conversão manual
              const options =
                fieldData.options ||
                fieldData.field_configuration?.options ||
                fieldData.custom_field?.field_configuration?.options ||
                []
              const option = options.find((opt: any) => opt.value === value)
              value = option
                ? { label: option.label, value: option.value }
                : { label: value, value: value }
            }
          } else {
            // Converter string para objeto (compatibilidade com formato antigo)
            const options =
              fieldData.options ||
              fieldData.field_configuration?.options ||
              fieldData.custom_field?.field_configuration?.options ||
              []
            const option = options.find((opt: any) => opt.value === value)
            value = option
              ? { label: option.label, value: option.value }
              : { label: value, value: value }
          }
        }
        // Se já é objeto {label, value}, manter como está
      } else if (fieldData.field_type === 'accept') {
        // Ensure boolean value
        value = Boolean(value)
      } else if (fieldData.field_type === 'upload') {
        // Objeto do arquivo (ou array deles) vira uuid (ou array de uuids)
        value = normalizeUploadSubmitValue(value)
      }

      formValues[fieldName] = value
    })

    return formValues
  }

  const saveConfiguration = async (
    configs: Array<{
      custom_field_uuid: string
      required: boolean
      position: number
    }>
  ): Promise<boolean> => {
    try {
      setLoading(true)
      setError(null)

      const payload = {
        context_type: contextType,
        custom_field_configs: configs
      }

      const response = await DataService({
        type: 'POST',
        url: '/v3/customers/custom_field_configs',
        data: payload
      })

      if (response.status < 400 && !response.error) {
        await loadCustomFields()
        return true
      } else {
        setError(response.message || 'Erro ao salvar configuração')
        return false
      }
    } catch (err) {
      console.error('Erro ao salvar configuração:', err)
      setError('Erro ao salvar configuração')
      return false
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (autoLoad) {
      loadCustomFields()
    }
  }, [contextType, entityUuid, autoLoad, uuid])

  return {
    customFields,
    loading,
    error,
    loadCustomFields,
    generateValidationSchema,
    processFormData,
    populateFormValues,
    saveConfiguration
  }
}

export default useCustomFields
